import type { AuthSession, AuthUser } from '@/api/models';
import { isJwtExpired } from './jwt';

/**
 * The **seller** session: the per-shift access token (+ its refresh token) and
 * the signed-in user, obtained via `POST /auth/pin`. Persisted as a JSON blob
 * under one key. The independent, long-lived **device** session lives separately
 * (see `deviceSession.ts`) and is never touched here — locking a shift clears
 * this session but keeps the terminal enrolled.
 */
const AUTH_SESSION_STORAGE_KEY = 'bos.auth.session';

const hasWindowStorage = () =>
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readSession = (): AuthSession | null => {
    if (!hasWindowStorage()) {
        return null;
    }
    const rawValue = window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!rawValue) {
        return null;
    }
    try {
        const parsed = JSON.parse(rawValue) as Partial<AuthSession>;
        if (
            parsed &&
            typeof parsed.accessToken === 'string' &&
            parsed.accessToken.length > 0 &&
            typeof parsed.refreshToken === 'string' &&
            parsed.refreshToken.length > 0 &&
            parsed.user
        ) {
            return parsed as AuthSession;
        }
    } catch {
        // Fall through to the cleanup below.
    }
    // A legacy bare-token string or otherwise unparseable blob: drop it so the
    // guard treats the terminal as signed out rather than half-authenticated.
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    return null;
};

export const getAccessToken = (): string | null =>
    readSession()?.accessToken ?? null;

export const getRefreshToken = (): string | null =>
    readSession()?.refreshToken ?? null;

export const getUser = (): AuthUser | null => readSession()?.user ?? null;

export const setSession = (session: AuthSession): void => {
    if (!hasWindowStorage()) {
        return;
    }
    window.localStorage.setItem(
        AUTH_SESSION_STORAGE_KEY,
        JSON.stringify(session),
    );
};

/** Every reason the app records for a sign-out (SEL-23). */
const SIGN_OUT_REASONS = ['sessionRevoked'] as const;

/**
 * Why the seller session ended, when the seller picker has to say so (SEL-23):
 * `sessionRevoked` means the server ended it, because the seller signed in on
 * another sale point. Stored beside the session because the interceptor leaves
 * with a full page load.
 */
export type SignOutReason = (typeof SIGN_OUT_REASONS)[number];

const SIGN_OUT_REASON_STORAGE_KEY = 'bos.auth.signOutReason';

const isSignOutReason = (value: string | null): value is SignOutReason =>
    SIGN_OUT_REASONS.some((reason) => reason === value);

/**
 * Drops the seller session. A sign-out replaces the recorded reason, so the
 * picker only explains the sign-out that just happened: pass `reason` when the
 * seller must be told why. Clearing when no session is stored is not a
 * sign-out and leaves the reason alone: a late 401 still in flight after a
 * revoked session must not erase why it ended.
 */
export const clearAuthSession = (reason?: SignOutReason): void => {
    if (!hasWindowStorage()) {
        return;
    }
    const hadSession =
        window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY) !== null;
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    if (reason) {
        window.localStorage.setItem(SIGN_OUT_REASON_STORAGE_KEY, reason);
    } else if (hadSession) {
        clearSignOutReason();
    }
};

/** The reason recorded by the last sign-out, if it is one the app knows. */
export const getSignOutReason = (): SignOutReason | null => {
    if (!hasWindowStorage()) {
        return null;
    }
    const stored = window.localStorage.getItem(SIGN_OUT_REASON_STORAGE_KEY);
    return isSignOutReason(stored) ? stored : null;
};

/** Forgets the recorded reason once the seller picker has moved on from it. */
export const clearSignOutReason = (): void => {
    if (!hasWindowStorage()) {
        return;
    }
    window.localStorage.removeItem(SIGN_OUT_REASON_STORAGE_KEY);
};

/**
 * `true` while a usable seller session exists: a stored refresh token. The
 * access token lapses every 15 minutes, so its expiry says nothing about
 * whether the user is still signed in. The server is the judge: a rejected
 * refresh clears the session (see `authInterceptors.ts`).
 */
export const isAuthenticated = (): boolean => getRefreshToken() !== null;

/**
 * `true` when the stored access JWT can still be sent. An undecodable /
 * `exp`-less token counts as expired (see `isJwtExpired`).
 */
export const hasLiveAccessToken = (): boolean =>
    !isJwtExpired(getAccessToken());
